import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { adminAuthHeader } from '../../../data/api';
import { MiloNotebookItems } from '../_shared/MiloNotebookItems';
import { isIntelligenceNotebookEnabled } from '../intelligence-notebook/intelligenceNotebookFlag';
import './event-program.css';

/**
 * Event Program — day-of run-of-show module.
 * Phase 1: the Program builder is live (fetch + full CRUD against /api/program,
 * owners from Vendors, guest count + venue hard-stop in the rail, soft/hard pins,
 * reorder). Insights / Speech / Live / Share are scaffolded and fill in Phases 2–6.
 */

const API = process.env.REACT_APP_BACKEND_URL;
// Admin auth header — matches the rest of the admin app (api.js / Milo panels / other tabs).
// Write endpoints (PUT /api/program, the Milo draft apply/undo, speeches) require it or they 401.
// Evaluated PER-REQUEST so the real admin token (set at login) is read, not a stale module-load value.
const ADMIN_AUTH = () => adminAuthHeader();
const TYPES = ['reception', 'ceremony', 'dining', 'program', 'highlight', 'send-off'];
const TYPE_ICON = { reception: '🥂', ceremony: '🕊', dining: '🍽', program: '🎞', highlight: '🎂', 'send-off': '✨' };
const TYPE_LABEL = { reception: 'Reception', ceremony: 'Ceremony', dining: 'Dining', program: 'Program', highlight: 'Highlight', 'send-off': 'Send-off' };
const OWNER_PRESETS = ['Host', 'MC', 'Officiant', 'Family'];

function hhmmTo12(s) {
  if (!s || !String(s).includes(':')) return '';
  const [h, m] = String(s).split(':').map(Number);
  const ap = h >= 12 ? 'pm' : 'am';
  const h12 = (h % 12) || 12;
  return `${h12}:${String(m).padStart(2, '0')}${ap}`;
}
function hhmmToMin(s) {
  if (!s || !String(s).includes(':')) return null;
  const [h, m] = String(s).split(':').map(Number);
  return h * 60 + m;
}
function clockNow(d) { return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; }
// point on the hero arc (cubic Bezier) at t∈[0,1], viewBox 1000x180
function cubicPoint(t) {
  const mt = 1 - t;
  const x = mt * mt * mt * 60 + 3 * mt * mt * t * 250 + 3 * mt * t * t * 750 + t * t * t * 940;
  const y = mt * mt * mt * 152 + 3 * mt * mt * t * 34 + 3 * mt * t * t * 34 + t * t * t * 152;
  return { x, y };
}

const BLANK = () => ({ title: '', type: 'program', duration_min: 30, start_mode: 'soft', hard_start_time: '', owners: [], notes: '' });

export default function EventProgramPage() {
  const [program, setProgram] = useState({ program_date: null, program_start_time: '17:00', program_end_time: '', items: [], summary: {} });
  const [vendors, setVendors] = useState([]);
  const [guestCount, setGuestCount] = useState(null);
  const [insights, setInsights] = useState(null);
  const [tab, setTab] = useState('program');
  const [editing, setEditing] = useState(null);   // null=closed; {item, isNew}
  const [confirmDel, setConfirmDel] = useState(null); // the item pending delete
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);     // Milo "draft my program"
  const [appliedDraftId, setAppliedDraftId] = useState(null); // shows the Undo bar

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/program`);
      setProgram(r.data);
    } catch (e) { /* keep defaults */ }
    try {
      const v = await axios.get(`${API}/api/vendors`);
      setVendors(Array.isArray(v.data) ? v.data : (v.data?.vendors || []));
    } catch (e) { /* optional */ }
    try {
      const g = await axios.get(`${API}/api/dashboard/attending-breakdown`);
      const b = g.data || {};
      // /dashboard/attending-breakdown returns { adults:[…], children:[…], totals:{adults,children} }.
      // adults/children are ARRAYS — never add them (array+array stringifies to "[object Object]…").
      // The real headcount is totals.* (party-aggregate rows already summed by aggregate_count).
      const t = b.totals || {};
      const c = (Number(t.adults) || 0) + (Number(t.children) || 0);
      setGuestCount(c > 0 ? c : null);
    } catch (e) { /* optional */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    if (tab === 'insights' && insights === null && !loading) {
      axios.get(`${API}/api/program/insights`).then((r) => setInsights(r.data)).catch(() => setInsights({ error: true }));
    }
  }, [tab, insights, loading]);

  // Persist the whole program (anchor + items). Resolved times come back computed.
  const save = useCallback(async (next) => {
    setSaving(true);
    try {
      const payload = {
        program_date: next.program_date ?? program.program_date,
        program_start_time: next.program_start_time ?? program.program_start_time,
        program_end_time: next.program_end_time ?? program.program_end_time,
        items: (next.items ?? program.items).map((it, i) => ({
          id: it.id, order: i, title: it.title, type: it.type,
          duration_min: Number(it.duration_min) || 0,
          start_mode: it.start_mode || 'soft',
          hard_start_time: it.start_mode === 'hard' ? (it.hard_start_time || null) : null,
          owners: it.owners || [], notes: it.notes || '',
        })),
      };
      const r = await axios.put(`${API}/api/program`, payload, { headers: ADMIN_AUTH() });
      setProgram(r.data.program);
      setInsights(null); // invalidate — the assessment depends on the program
    } catch (e) { /* surface later */ }
    setSaving(false);
  }, [program]);

  const saveMoment = (form) => {
    const items = [...program.items];
    if (editing.isNew) items.push(form);
    else {
      const idx = items.findIndex((it) => it.id === editing.item.id);
      if (idx >= 0) items[idx] = { ...items[idx], ...form };
      else items.push(form);
    }
    setEditing(null);
    save({ items });
  };
  const removeMoment = (item) => { setConfirmDel(null); save({ items: program.items.filter((it) => it.id !== item.id) }); };
  const onDraftApplied = useCallback((id) => { setDraftOpen(false); setAppliedDraftId(id); fetchAll(); }, [fetchAll]);
  // Live Mode: "we're actually starting this now" — re-pin a moment to the wall clock,
  // which re-anchors the soft cascade after it. A normal manual edit; no kill switch.
  const pinMomentNow = useCallback((item) => {
    const t = clockNow(new Date());
    const items = (program.items || []).map((it) => it.id === item.id
      ? { ...it, start_mode: 'hard', hard_start_time: t } : it);
    save({ items });
  }, [program, save]);
  const undoDraft = useCallback(async (id) => {
    try { await axios.post(`${API}/api/program/milo/draft/${id}/undo`, {}, { headers: ADMIN_AUTH() }); } catch (e) { /* keep state */ }
    setAppliedDraftId(null); setInsights(null); fetchAll();
  }, [fetchAll]);
  const moveMoment = (idx, dir) => {
    const items = [...program.items];
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    [items[idx], items[j]] = [items[j], items[idx]];
    save({ items });
  };

  const items = program.items || [];
  const n = items.length;
  const summary = program.summary || {};

  return (
    <div className="ep">
      {/* header */}
      <div className="ep-eb">Planning · The Evening</div>
      <div className="ep-title">Event Program</div>
      <div className="ep-meta">
        {program.program_date ? `${program.program_date} · ` : ''}
        {hhmmTo12(program.program_start_time) || '—'}
        {summary.computed_end ? ` – ${hhmmTo12(summary.computed_end)}` : ''}
        {` · ${n} moment${n !== 1 ? 's' : ''}`}
      </div>
      <div style={{ height: 2, background: 'linear-gradient(90deg, #D4AF37 0%, #F5D061 30%, rgba(212,175,55,0.3) 60%, transparent 100%)', marginTop: 12, marginBottom: 4, boxShadow: '0 1px 4px rgba(212,175,55,0.2)' }} />

      {/* sub-tabs */}
      <div className="ep-subtabs">
        {[['program', '▤ Program'], ['insights', '✦ Insights'], ['speech', '🎤 Speech Studio'], ['live', '◷ Live'], ['share', '↗ Share']].map(([id, label]) => (
          <button key={id} className={`ep-stab ${tab === id ? 'on' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {loading ? <div className="ep-loading">Loading the program…</div> : (
        <>
          {/* ===================== PROGRAM ===================== */}
          <div className={`ep-view ${tab === 'program' ? 'on' : ''}`}>
            {appliedDraftId && (
              <div className="ep-undobar">
                <span><b>✦ Milo</b> drafted and applied this program. Tweak any moment, or</span>
                <button className="ep-ulink" onClick={() => undoDraft(appliedDraftId)}>undo it</button>
              </div>
            )}
            {/* arc hero */}
            <div className="ep-hero">
              <div className="ep-vig" />
              <div className="ep-hero-top">
                <div className="ep-he">The Evening</div>
                <h2>The flow of the <em>night</em></h2>
                <div className="ep-hs">Set each moment's length — times keep themselves in step. Pin the ones that must hit the clock.</div>
                <div className="ep-hm">
                  <span><b>{hhmmTo12(program.program_start_time) || '—'}</b> start</span>
                  {program.program_end_time ? <span><b>{hhmmTo12(program.program_end_time)}</b> close</span> : null}
                  <span><b>{n}</b> moments</span>
                  {summary.computed_end ? <span>ends <b>{hhmmTo12(summary.computed_end)}</b></span> : null}
                </div>
              </div>
              <div className="ep-arc">
                <svg viewBox="0 0 1000 180" preserveAspectRatio="none">
                  <defs><linearGradient id="epag" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#9C7B31" /><stop offset="50%" stopColor="#E4CE92" /><stop offset="100%" stopColor="#9C7B31" /></linearGradient></defs>
                  <path className="ep-arcp" d="M 60,152 C 250,34 750,34 940,152" />
                  {items.map((it, i) => {
                    const t = n > 1 ? i / (n - 1) : 0.5;
                    const p = cubicPoint(t);
                    const isPk = it.start_mode === 'hard';
                    return (
                      <g key={it.id || i}>
                        <circle className={`ep-ad ${isPk ? 'pk' : ''}`} cx={p.x} cy={p.y} r={isPk ? 6 : 4} />
                        {(i === 0 || i === n - 1 || isPk) && (
                          <text className={`ep-al ${isPk ? 'pk' : ''}`} x={p.x} y={p.y > 100 ? p.y + 20 : p.y - 12} textAnchor="middle">
                            {hhmmTo12(it.resolved_start)}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </svg>
              </div>
            </div>

            {/* context rail */}
            <div className="ep-rail">
              <div className="ep-rstat"><div className="k">Program starts</div><div className="v">{hhmmTo12(program.program_start_time) || '—'}</div></div>
              {guestCount != null && <div className="ep-rstat"><div className="k">Guests confirmed</div><div className="v">{guestCount}</div><div className="src">from Guests</div></div>}
              {program.program_end_time
                ? <div className="ep-rstat cstr"><div className="k">⬩ Hard stop</div><div className="v">Ends {hhmmTo12(program.program_end_time)}</div><div className="src">{summary.fits ? `${summary.cushion_minutes} min to spare` : 'over — trim'}</div></div>
                : <div className="ep-rstat"><div className="k">Runs until</div><div className="v">{hhmmTo12(summary.computed_end) || '—'}</div></div>}
              <div className="ep-rstat"><div className="k">Total length</div><div className="v">{summary.total_minutes || 0} <small>min</small></div></div>
            </div>

            {/* milo summary card (placeholder until Phase 3 wires /milo/agent) */}
            <div className="ep-milocard" onClick={() => setTab('insights')}>
              <div className="ep-orb">M</div>
              <div><div className="mt">Milo Insights</div><div className="ms">Open the assessment of your evening's flow &amp; tone <b>→</b></div></div>
              <div className="ep-chev">›</div>
            </div>

            {/* timeline */}
            <div className="ep-seclab">The timeline</div>
            {n === 0 ? (
              <div className="ep-empty">
                <div>No moments yet. Build the evening yourself, or let Milo draft a realistic starting point you can shape.</div>
                <button className="ep-btn gold" style={{ marginTop: 14 }} onClick={() => setDraftOpen(true)}>✦ Draft my program with Milo</button>
              </div>
            ) : (
              <div className="ep-tl">
                {items.map((it, i) => {
                  const tcls = `ep-c-${it.type || 'program'}`;
                  return (
                    <div className="ep-mom" key={it.id || i}>
                      <div className={`ep-mtime ${it.start_mode === 'hard' ? 'hard' : ''}`}>
                        <div className="t">{hhmmTo12(it.resolved_start)}</div>
                        {it.start_mode === 'hard' && <div className="pin">⬩ pinned</div>}
                        <div className="ep-mdot" />
                      </div>
                      <div className={`ep-mcard ${it.start_mode === 'hard' ? 'hard' : ''}`} onClick={() => setEditing({ item: it, isNew: false })}>
                        <div className="ep-mic">{TYPE_ICON[it.type] || '•'}</div>
                        <div className="ep-mmain">
                          <div className="ep-mhead">
                            <span className={`ep-chip ${tcls}`}><i />{TYPE_LABEL[it.type] || it.type}</span>
                            <span className="ep-mdur">{it.duration_min} min</span>
                          </div>
                          <div className="ep-mttl">{it.title || 'Untitled moment'}</div>
                          {(it.owners && it.owners.length > 0) && (
                            <div className="ep-mown">{it.owners.map((o, k) => <span key={k} className={k === 0 ? 'lead' : ''}>{o.name}{o.vendor_id ? ' ↗' : ''}</span>)}</div>
                          )}
                        </div>
                        <div className="ep-mtools" onClick={(e) => e.stopPropagation()}>
                          <button className="ep-mtool" title="Move up" onClick={() => moveMoment(i, -1)}>↑</button>
                          <button className="ep-mtool" title="Move down" onClick={() => moveMoment(i, 1)}>↓</button>
                          <button className="ep-mtool" title="Edit" onClick={() => setEditing({ item: it, isNew: false })}>✎</button>
                          <button className="ep-mtool del" title="Delete" onClick={() => setConfirmDel(it)}>🗑</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <button className="ep-add" onClick={() => setEditing({ item: BLANK(), isNew: true })}>＋ Add a moment</button>
            <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="ep-btn" onClick={() => setEditing({ item: { ...BLANK(), _anchor: true }, isNew: false, anchor: true })}>◷ Edit start / end time</button>
              <button className="ep-btn" onClick={() => setDraftOpen(true)}>✦ Draft with Milo</button>
            </div>
            {isIntelligenceNotebookEnabled() && (<div style={{ marginTop: 16 }}><MiloNotebookItems kind="program_moment" title="Milo Suggestions" /></div>)}
          </div>

          {/* ===================== INSIGHTS (Phase 2, live + deterministic) ===================== */}
          <div className={`ep-view ${tab === 'insights' ? 'on' : ''}`}>
            <InsightsView data={insights} />
          </div>

          {/* ===================== LIVE MODE (Phase 4, day-of now-line) ===================== */}
          <div className={`ep-view ${tab === 'live' ? 'on' : ''}`}>
            {tab === 'live' && <LiveView program={program} onPin={pinMomentNow} saving={saving} />}
          </div>

          {/* ===================== SPEECH STUDIO (Phase 5) ===================== */}
          <div className={`ep-view ${tab === 'speech' ? 'on' : ''}`}>
            {tab === 'speech' && <SpeechView program={program} />}
          </div>

          {/* ===================== SHARE (Phase 6) ===================== */}
          <div className={`ep-view ${tab === 'share' ? 'on' : ''}`}>
            {tab === 'share' && <ShareView program={program} />}
          </div>
        </>
      )}

      {/* edit / add moment modal */}
      {editing && !editing.anchor && (
        <MomentEditor
          initial={editing.item} isNew={editing.isNew} vendors={vendors} saving={saving}
          onClose={() => setEditing(null)} onSave={saveMoment}
          onDelete={editing.isNew ? null : () => { setConfirmDel(editing.item); setEditing(null); }}
        />
      )}

      {/* anchor (start/end) editor */}
      {editing && editing.anchor && (
        <AnchorEditor program={program} onClose={() => setEditing(null)}
          onSave={(a) => { setEditing(null); save(a); }} />
      )}

      {/* delete confirm */}
      {confirmDel && (
        <div className="ep-ov" onClick={(e) => { if (e.target.className === 'ep-ov') setConfirmDel(null); }}>
          <div className="ep-modal" style={{ maxWidth: 420 }}>
            <div className="ep-mhead"><div className="mt">Delete this moment?</div><button className="ep-mx" onClick={() => setConfirmDel(null)}>✕</button></div>
            <div className="ep-mbody"><div className="ep-confirm">Remove <b>“{confirmDel.title || 'Untitled moment'}”</b> — the moments after shift up automatically; pinned moments hold their time.</div></div>
            <div className="ep-mfoot"><button className="ep-btn" onClick={() => setConfirmDel(null)}>Keep it</button><button className="ep-btn danger solid" onClick={() => removeMoment(confirmDel)}>Delete moment</button></div>
          </div>
        </div>
      )}

      {/* Milo draft my program (propose → review → apply) */}
      {draftOpen && (
        <DraftModal guestCount={guestCount} onClose={() => setDraftOpen(false)} onApplied={onDraftApplied} />
      )}
    </div>
  );
}

/* ---- moment editor ---- */
function MomentEditor({ initial, isNew, vendors, saving, onClose, onSave, onDelete }) {
  const [f, setF] = useState({ ...BLANK(), ...initial, owners: initial.owners || [] });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const ownerNames = new Set((f.owners || []).map((o) => o.name));
  const toggleOwner = (name, vendor_id) => {
    const has = ownerNames.has(name);
    const next = has ? f.owners.filter((o) => o.name !== name) : [...(f.owners || []), { name, vendor_id }];
    set('owners', next);
  };
  return (
    <div className="ep-ov" onClick={(e) => { if (e.target.className === 'ep-ov') onClose(); }}>
      <div className="ep-modal">
        <div className="ep-mhead"><div className="mt">{isNew ? 'Add a moment' : 'Edit moment'}</div><button className="ep-mx" onClick={onClose}>✕</button></div>
        <div className="ep-mbody">
          <div className="ep-fld"><label>Moment</label><input className="ep-inp" value={f.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Cake Cutting & Champagne" autoFocus /></div>
          <div className="ep-row2">
            <div className="ep-fld"><label>Length (min)</label><input className="ep-inp" type="number" min="0" value={f.duration_min} onChange={(e) => set('duration_min', e.target.value)} /></div>
            <div className="ep-fld"><label>Type</label>
              <div className="ep-chiprow">{TYPES.map((t) => <span key={t} className={`ep-pick ${f.type === t ? 'on' : ''}`} onClick={() => set('type', t)}>{TYPE_LABEL[t]}</span>)}</div>
            </div>
          </div>
          <div className="ep-fld"><label>Start</label>
            <div className="ep-seg">
              <div className={`ep-sgb ${f.start_mode === 'soft' ? 'on' : ''}`} onClick={() => set('start_mode', 'soft')}>Auto<small>after the moment before</small></div>
              <div className={`ep-sgb ${f.start_mode === 'hard' ? 'on' : ''}`} onClick={() => set('start_mode', 'hard')}>Pinned<small>holds a clock time</small></div>
            </div>
            {f.start_mode === 'hard' && (
              <input className="ep-inp" style={{ marginTop: 9 }} type="time" value={f.hard_start_time || ''} onChange={(e) => set('hard_start_time', e.target.value)} />
            )}
            <div className="ep-note">Pinned moments anchor the schedule — everything before flows up to them; everything after follows on.</div>
          </div>
          <div className="ep-fld"><label>Who's leading</label>
            <div className="ep-chiprow">
              {OWNER_PRESETS.map((name) => <span key={name} className={`ep-pick ${ownerNames.has(name) ? 'on' : ''}`} onClick={() => toggleOwner(name)}>{name}</span>)}
              {vendors.slice(0, 12).map((v) => {
                const name = v.company_name || v.name; if (!name) return null;
                return <span key={v.id || name} className={`ep-pick ${ownerNames.has(name) ? 'on' : ''}`} onClick={() => toggleOwner(name, v.id)}>{name} ↗</span>;
              })}
            </div>
          </div>
          <div className="ep-fld"><label>Notes</label><input className="ep-inp" value={f.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Cues, reminders…" /></div>
        </div>
        <div className="ep-mfoot">
          {onDelete && <button className="ep-btn danger" onClick={onDelete}>🗑 Delete</button>}
          <button className="ep-btn" onClick={onClose}>Cancel</button>
          <button className="ep-btn gold" disabled={saving} onClick={() => onSave(f)}>{saving ? 'Saving…' : (isNew ? 'Add moment' : 'Save moment')}</button>
        </div>
      </div>
    </div>
  );
}

/* ---- anchor (start/end/date) editor ---- */
function AnchorEditor({ program, onClose, onSave }) {
  const [start, setStart] = useState(program.program_start_time || '17:00');
  const [end, setEnd] = useState(program.program_end_time || '');
  const [date, setDate] = useState(program.program_date || '');
  return (
    <div className="ep-ov" onClick={(e) => { if (e.target.className === 'ep-ov') onClose(); }}>
      <div className="ep-modal" style={{ maxWidth: 440 }}>
        <div className="ep-mhead"><div className="mt">Program start &amp; end</div><button className="ep-mx" onClick={onClose}>✕</button></div>
        <div className="ep-mbody">
          <div className="ep-fld"><label>Date</label><input className="ep-inp" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div className="ep-row2">
            <div className="ep-fld"><label>Program starts</label><input className="ep-inp" type="time" value={start} onChange={(e) => setStart(e.target.value)} /></div>
            <div className="ep-fld"><label>Venue hard stop</label><input className="ep-inp" type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          </div>
          <div className="ep-note">Everything flows from the start time; the hard stop is the venue's close — Milo will warn if the evening runs past it.</div>
        </div>
        <div className="ep-mfoot"><button className="ep-btn" onClick={onClose}>Cancel</button><button className="ep-btn gold" onClick={() => onSave({ program_date: date, program_start_time: start, program_end_time: end })}>Save</button></div>
      </div>
    </div>
  );
}

/* ---- Insights view (Phase 2: grounded, deterministic, evidence-backed) ---- */
function InsightsView({ data }) {
  if (!data) return <div className="ep-loading">Assessing the evening…</div>;
  if (data.error || data.overall === 'Empty') {
    return <div className="ep-ph"><div className="pt">Milo Insights</div><div className="pd">Add a few moments to your program, then the assessment appears here — grounded in your actual timeline.</div></div>;
  }
  const d = data.dimensions || {};
  const dot = { Flow: '#789A82', Emotion: '#B98C3C', Feasibility: '#7C92B0', Balance: '#C6A75A' };
  // Headline must agree with the "Worth a look" column. Don't call it calm while risks are listed.
  const riskCount = (data.risks || []).length;
  const overflows = data.summary && data.summary.fits === false;
  const verdictLine = overflows
    ? 'Runs past the venue hard stop — see "Worth a look".'
    : riskCount > 0
      ? `${riskCount} ${riskCount === 1 ? 'thing' : 'things'} worth a look before the night feels settled — see "Worth a look".`
      : 'A calm, well-paced evening.';
  return (
    <div className="ep-ins-grid">
      <div>
        <div className="ep-verdict">
          <div className="vk">Overall assessment</div>
          <div className="vv">{data.overall}</div>
          <div className="vs">{verdictLine}</div>
          <div className="ep-dims">
            {[['Flow', d.flow], ['Emotion', d.emotion], ['Feasibility', d.feasibility], ['Balance', d.balance]].map(([k, v]) => (
              <div className="ep-dim" key={k}><div className="dk"><i style={{ background: dot[k] }} />{k}</div><div className="dv">{v || '—'}</div></div>
            ))}
          </div>
        </div>
        {data.missing && data.missing.length > 0 && (
          <>
            <div className="ep-seclab">Possibly missing</div>
            {data.missing.map((m, i) => <div className="ep-ins-row" key={i}><span className="ic">✦</span><span>{m.text}</span></div>)}
          </>
        )}
      </div>
      <div className="ep-card">
        <div className="ep-seclab" style={{ marginTop: 0 }}>Worth a look</div>
        {(data.risks || []).length === 0
          ? <div className="ep-ins-row"><span className="ic">✓</span><span>No pacing or tone risks detected.</span></div>
          : data.risks.map((r, i) => <div className="ep-ins-row" key={i}><span className="ic">⚠</span><span>{r.text}{r.evidence ? <em style={{ color: 'var(--ep-mute)' }}> · {r.evidence}</em> : null}</span></div>)}
        {(data.suggestions || []).length > 0 && <div className="ep-seclab">Milo suggests</div>}
        {(data.suggestions || []).map((s, i) => <div className="ep-sug" key={i}><div className="st">{s.text}</div></div>)}
        <div className="ep-note" style={{ marginTop: 12 }}>Grounded in your program — every note traces to a real number. Milo's tone &amp; cultural read layer on next, with the API key.</div>
      </div>
    </div>
  );
}

/* ---- Milo "draft my program" — propose → review → apply, governed + graceful ---- */
// The draft now runs server-side as a background job: POST kicks it off and returns
// { job_id } immediately, then we poll GET …/job/{job_id} every ~2s until it's done or
// errors. This keeps the slow LLM generation off the request so the gateway never times
// out mid-draft. Capped at ~2.5 min so a stuck job surfaces as a graceful error, not a hang.
const DRAFT_POLL_MS = 2000;
const DRAFT_POLL_MAX_MS = 150000; // ~2.5 min

function DraftModal({ guestCount, onClose, onApplied }) {
  const [step, setStep] = useState('brief');   // brief | loading | review | nokey | error | applying
  const [brief, setBrief] = useState({ event_type: '', guest_count: guestCount || '', tone: '', must_haves: '', cultural_notes: '' });
  const [draft, setDraft] = useState(null);
  const [err, setErr] = useState('');
  const set = (k, v) => setBrief((s) => ({ ...s, [k]: v }));

  // Cancel any in-flight polling if the user closes the modal (or it unmounts) mid-draft,
  // so we never setState on an unmounted component or keep hitting the GET after close.
  const aliveRef = React.useRef(true);
  const pollTimerRef = React.useRef(null);
  // Duplicate-click guard: true only while a draft job is in flight (step === 'loading'),
  // so a fast double-click (or a stray re-trigger) can never start a second job.
  const inFlightRef = React.useRef(false);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; if (pollTimerRef.current) clearTimeout(pollTimerRef.current); };
  }, []);
  useEffect(() => { if (step !== 'loading') inFlightRef.current = false; }, [step]);

  const ask = async () => {
    if (inFlightRef.current) return;   // a draft job is already in flight — ignore the duplicate click
    inFlightRef.current = true;
    setStep('loading'); setErr('');
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
    const payload = {
      event_type: brief.event_type || null,
      guest_count: brief.guest_count ? Number(brief.guest_count) : null,
      tone: brief.tone || null,
      must_haves: brief.must_haves ? brief.must_haves.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean) : [],
      cultural_notes: brief.cultural_notes || null,
    };
    // 1) Kick off the background job. Returns immediately with { job_id }.
    let jobId;
    try {
      const r = await axios.post(`${API}/api/program/milo/draft`, payload, { headers: ADMIN_AUTH() });
      if (!aliveRef.current) return;
      const d = r.data || {};
      // A fast/synchronous response is still honored (key missing, or a job that finished inline).
      if (d.configured === false) { setStep('nokey'); return; }
      if (d.draft) { setDraft(d.draft); setStep('review'); return; }
      if (d.status === 'failed' || d.error) { setErr(d.error || 'Milo could not draft a program just now.'); setStep('error'); return; }
      jobId = d.job_id;
      if (!jobId) { setErr('Milo could not draft a program just now.'); setStep('error'); return; }
    } catch (e) { if (aliveRef.current) { setErr('Something went wrong reaching Milo.'); setStep('error'); } return; }

    // 2) Poll the job until it's done/errors, or we hit the time cap.
    const startedAt = Date.now();
    const poll = async () => {
      if (!aliveRef.current) return;
      try {
        const r = await axios.get(`${API}/api/program/milo/draft/job/${jobId}`, { headers: ADMIN_AUTH() });
        if (!aliveRef.current) return;
        const d = r.data || {};
        if (d.configured === false) { setStep('nokey'); return; }
        if (d.status === 'succeeded' && d.draft) { setDraft(d.draft); setStep('review'); return; }
        if (d.status === 'failed') { setErr(d.error || 'Milo could not draft a program just now.'); setStep('error'); return; }
        // status === 'queued'|'running' (or anything inconclusive) → keep waiting, unless we're out of time.
        if (Date.now() - startedAt >= DRAFT_POLL_MAX_MS) {
          setErr('Milo is taking longer than expected. Please try again in a moment.'); setStep('error'); return;
        }
        pollTimerRef.current = setTimeout(poll, DRAFT_POLL_MS);
      } catch (e) {
        if (!aliveRef.current) return;
        // A transient blip shouldn't kill the whole draft — retry until the time cap, then surface.
        if (Date.now() - startedAt >= DRAFT_POLL_MAX_MS) { setErr('Something went wrong reaching Milo.'); setStep('error'); return; }
        pollTimerRef.current = setTimeout(poll, DRAFT_POLL_MS);
      }
    };
    pollTimerRef.current = setTimeout(poll, DRAFT_POLL_MS);
  };

  const apply = async () => {
    if (!draft) return;
    setStep('applying'); setErr('');
    try {
      await axios.post(`${API}/api/program/milo/draft/${draft.id}/apply`, { confirm: true }, { headers: ADMIN_AUTH() });
      onApplied(draft.id);
    } catch (e) {
      const code = e && e.response && e.response.status;
      if (code === 403) setErr('Milo actions are turned off. Enable them in AI settings, then apply.');
      else setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not apply the draft.');
      setStep('review');
    }
  };

  return (
    <div className="ep-ov" onClick={(e) => { if (e.target.className === 'ep-ov') onClose(); }}>
      <div className="ep-modal ep-draft">
        <div className="ep-mhead"><div className="mt"><span className="ep-orb sm">M</span> Draft my program</div><button className="ep-mx" onClick={onClose}>✕</button></div>

        {step === 'brief' && (
          <>
            <div className="ep-mbody">
              <div className="ep-dnote">Milo proposes a realistic run-of-show grounded in your brief. Nothing is saved until you review and apply.</div>
              <div className="ep-fld"><label>What's the occasion?</label><input className="ep-inp" value={brief.event_type} onChange={(e) => set('event_type', e.target.value)} placeholder="e.g. 80th birthday dinner" autoFocus /></div>
              <div className="ep-row2">
                <div className="ep-fld"><label>Guests (approx)</label><input className="ep-inp" type="number" min="0" value={brief.guest_count} onChange={(e) => set('guest_count', e.target.value)} placeholder={guestCount ? String(guestCount) : '—'} /></div>
                <div className="ep-fld"><label>Tone</label><input className="ep-inp" value={brief.tone} onChange={(e) => set('tone', e.target.value)} placeholder="warm · formal · lively" /></div>
              </div>
              <div className="ep-fld"><label>Must include</label><input className="ep-inp" value={brief.must_haves} onChange={(e) => set('must_haves', e.target.value)} placeholder="cake cutting, family slideshow, first dance" /><div className="ep-fhint">Comma-separated — Milo won't drop these.</div></div>
              <div className="ep-fld"><label>Cultural / family context <small>(optional)</small></label><textarea className="ep-inp ep-ta" value={brief.cultural_notes} onChange={(e) => set('cultural_notes', e.target.value)} placeholder="e.g. Catholic blessing before dinner; family tribute" /></div>
            </div>
            <div className="ep-mfoot"><button className="ep-btn" onClick={onClose}>Cancel</button><button className="ep-btn gold" onClick={ask}>✦ Ask Milo to draft</button></div>
          </>
        )}

        {step === 'loading' && <div className="ep-mbody"><div className="ep-loading">Milo is shaping your evening…</div></div>}
        {step === 'applying' && <div className="ep-mbody"><div className="ep-loading">Applying to your program…</div></div>}

        {step === 'nokey' && (
          <>
            <div className="ep-mbody"><div className="ep-ph" style={{ padding: '22px 6px' }}>
              <div className="pt">Milo's AI brain isn't connected</div>
              <div className="pd">Add an <code>ANTHROPIC_API_KEY</code> to the backend to let Milo draft programs. Until then, build yours manually — every other feature works without it.</div>
            </div></div>
            <div className="ep-mfoot"><button className="ep-btn" onClick={onClose}>Close</button></div>
          </>
        )}

        {step === 'error' && (
          <>
            <div className="ep-mbody"><div className="ep-derr">{err}</div></div>
            <div className="ep-mfoot"><button className="ep-btn" onClick={() => setStep('brief')}>Back</button><button className="ep-btn gold" onClick={ask}>Try again</button></div>
          </>
        )}

        {step === 'review' && draft && (
          <>
            <div className="ep-mbody">
              {err && <div className="ep-derr">{err}</div>}
              {draft.summary && <div className="ep-dsum">{draft.summary}</div>}
              <div className="ep-seclab" style={{ marginTop: 4 }}>Proposed — {(draft.items || []).length} moments</div>
              <div className="ep-dlist">
                {(draft.items || []).map((it, i) => (
                  <div className="ep-drow" key={i}>
                    <span className="ep-dic">{TYPE_ICON[it.type] || '•'}</span>
                    <span className="ep-dttl">{it.title}</span>
                    <span className="ep-ddur">{it.duration_min}m{it.start_mode === 'hard' && it.hard_start_time ? ` · ⬩${hhmmTo12(it.hard_start_time)}` : ''}</span>
                  </div>
                ))}
              </div>
              {(draft.clarifying_questions || []).length > 0 && (
                <><div className="ep-seclab">Milo wants to confirm</div>{draft.clarifying_questions.map((q, i) => <div className="ep-ins-row" key={i}><span className="ic">?</span><span>{q}</span></div>)}</>
              )}
              {(draft.cultural_notes || []).length > 0 && (
                <><div className="ep-seclab">Cultural notes applied</div>{draft.cultural_notes.map((c, i) => <div className="ep-ins-row" key={i}><span className="ic">✦</span><span>{c}</span></div>)}</>
              )}
              <div className="ep-note" style={{ marginTop: 12 }}>Applying replaces your current moments. You can undo right after.</div>
            </div>
            <div className="ep-mfoot"><button className="ep-btn" onClick={onClose}>Discard</button><button className="ep-btn gold" onClick={apply}>Apply to program</button></div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---- Live Mode — the serene day-of now-line (Phase 4) ---- */
function LiveView({ program, onPin, saving }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const items = program.items || [];
  if (items.length === 0) {
    return <div className="ep-ph"><div className="pt">Live Mode</div><div className="pd">Build your program first — then on the day, this becomes a calm now-line that follows the evening minute by minute.</div></div>;
  }
  const nowMin = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
  const rows = items
    .map((it) => ({ it, s: hhmmToMin(it.resolved_start), e: hhmmToMin(it.resolved_end) }))
    .filter((r) => r.s != null);
  if (rows.length === 0) {
    return <div className="ep-ph"><div className="pt">Live Mode</div><div className="pd">Set a start time on your program to begin the live now-line.</div></div>;
  }
  const first = rows[0];
  const last = rows[rows.length - 1];
  const curIdx = rows.findIndex((r) => nowMin >= r.s && (r.e == null || nowMin < r.e));
  const state = curIdx >= 0 ? 'live' : (nowMin < first.s ? 'pre' : 'done');
  const cur = curIdx >= 0 ? rows[curIdx] : null;
  const nextRow = rows.find((r) => r.s > nowMin);
  const fmt = (mins) => {
    const m = Math.max(0, Math.round(mins));
    return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m} min`;
  };

  return (
    <div className="ep-live">
      <div className="ep-liveclock"><span className="dot" />{hhmmTo12(clockNow(now))}<small>now</small></div>

      {state === 'pre' && (
        <div className="ep-nowcard pre">
          <div className="ep-nlab">The evening begins in</div>
          <div className="ep-ncount">{fmt(first.s - nowMin)}</div>
          <div className="ep-nnext">First: <b>{first.it.title}</b> · {hhmmTo12(first.it.resolved_start)}</div>
          <button className="ep-btn gold" disabled={saving} onClick={() => onPin(first.it)}>▶ Start now</button>
        </div>
      )}

      {state === 'done' && (
        <div className="ep-nowcard done">
          <div className="ep-nlab">The evening is complete</div>
          <div className="ep-ncount">✦</div>
          <div className="ep-nnext">{rows.length} moments · ended {hhmmTo12(last.it.resolved_end)}</div>
        </div>
      )}

      {state === 'live' && cur && (
        <div className="ep-nowcard">
          <div className="ep-nlab">Happening now</div>
          <div className="ep-nttl">{TYPE_ICON[cur.it.type] || ''} {cur.it.title}</div>
          {cur.e != null && (
            <>
              <div className="ep-nbarw"><div className="ep-nbar" style={{ width: `${Math.min(100, Math.max(0, ((nowMin - cur.s) / Math.max(1, cur.e - cur.s)) * 100))}%` }} /></div>
              <div className="ep-ncount sm">{fmt(cur.e - nowMin)} left</div>
            </>
          )}
          {(cur.it.owners && cur.it.owners.length > 0) && <div className="ep-nown">{cur.it.owners.map((o, k) => <span key={k}>{o.name}</span>)}</div>}
          {cur.it.notes && <div className="ep-nnote">{cur.it.notes}</div>}
        </div>
      )}

      {nextRow && (
        <div className="ep-upnext">
          <div className="ep-ul">Up next · in {fmt(nextRow.s - nowMin)}</div>
          <div className="ep-um">
            <span className="ic">{TYPE_ICON[nextRow.it.type] || '•'}</span>
            <span className="t">{nextRow.it.title}</span>
            <span className="at">{hhmmTo12(nextRow.it.resolved_start)}</span>
            <button className="ep-pinnow" disabled={saving} onClick={() => onPin(nextRow.it)} title="We're starting this now — re-time the rest">▶ now</button>
          </div>
        </div>
      )}

      <div className="ep-seclab">Running order</div>
      <div className="ep-runlist">
        {rows.map((r, i) => {
          const cls = i === curIdx ? 'cur' : (r.e != null && nowMin >= r.e ? 'past' : 'fut');
          return (
            <div className={`ep-runrow ${cls}`} key={r.it.id || i}>
              <div className="rt">{hhmmTo12(r.it.resolved_start)}</div>
              <div className="rd" />
              <div className="rttl">{r.it.title}{r.it.start_mode === 'hard' ? <span className="rp">⬩</span> : null}</div>
              <div className="rdur">{r.it.duration_min}m</div>
            </div>
          );
        })}
      </div>
      <div className="ep-note">Times follow your program live. Tap <b>▶ now</b> on any moment when it actually begins — everything after re-times itself.</div>
    </div>
  );
}

/* ---- Speech Studio (Phase 5) — speakers + toasts, timing metrics + Milo coaching ---- */
const BLANK_SPEECH = () => ({ speaker_name: '', role: '', moment_id: '', target_minutes: 3, text: '' });

function SpeechView({ program }) {
  const [speeches, setSpeeches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);     // {item, isNew}
  const [confirmDel, setConfirmDel] = useState(null);
  const [coachFor, setCoachFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/api/program/speeches`); setSpeeches(r.data.speeches || []); }
    catch (e) { /* keep */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (list) => {
    try {
      const payload = { speeches: list.map((s) => ({
        id: s.id, speaker_name: s.speaker_name, role: s.role, moment_id: s.moment_id || null,
        target_minutes: (s.target_minutes !== '' && s.target_minutes != null) ? Number(s.target_minutes) : null,
        text: s.text || '' })) };
      const r = await axios.put(`${API}/api/program/speeches`, payload, { headers: ADMIN_AUTH() });
      setSpeeches(r.data.speeches || []);
    } catch (e) { /* surface later */ }
  };
  const saveSpeech = (form) => {
    const list = [...speeches];
    if (editing.isNew) list.push(form);
    else { const i = list.findIndex((s) => s.id === editing.item.id); if (i >= 0) list[i] = { ...list[i], ...form }; else list.push(form); }
    setEditing(null); save(list);
  };
  const removeSpeech = (s) => { setConfirmDel(null); save(speeches.filter((x) => x.id !== s.id)); };
  const momentName = (id) => { const m = (program.items || []).find((it) => it.id === id); return m ? m.title : null; };

  if (loading) return <div className="ep-loading">Loading the toasts…</div>;

  return (
    <div className="ep-speech">
      <div className="ep-shead">
        <div><div className="ep-he">The Words</div><h2 className="ep-sh2">Speeches &amp; toasts</h2></div>
        <button className="ep-btn gold" onClick={() => setEditing({ item: BLANK_SPEECH(), isNew: true })}>＋ Add a speaker</button>
      </div>

      {speeches.length === 0 ? (
        <div className="ep-empty"><div>No speeches yet. Add the speakers — Milo can time each toast and coach the words when you're ready.</div></div>
      ) : (
        <div className="ep-spgrid">
          {speeches.map((s) => {
            const m = s.metrics || {};
            const vcls = { 'On time': 'ok', Long: 'warn', Short: 'info', Empty: 'mute', 'No target': 'mute' }[m.verdict] || 'mute';
            return (
              <div className="ep-spcard" key={s.id}>
                <div className="ep-sptop">
                  <div className="ep-spwho"><div className="nm">{s.speaker_name || 'Unnamed speaker'}</div>{s.role && <div className="rl">{s.role}</div>}</div>
                  <div className="ep-sptools">
                    <button className="ep-mtool" title="Edit" onClick={() => setEditing({ item: s, isNew: false })}>✎</button>
                    <button className="ep-mtool del" title="Delete" onClick={() => setConfirmDel(s)}>🗑</button>
                  </div>
                </div>
                {s.moment_id && momentName(s.moment_id) && <div className="ep-splink">↳ {momentName(s.moment_id)}</div>}
                <div className="ep-spmeta">
                  <span className={`ep-vpill ${vcls}`}>{m.verdict || '—'}</span>
                  <span className="ep-spm">{m.est_minutes || 0} min{m.target_minutes ? ` / ${m.target_minutes}` : ''}</span>
                  <span className="ep-spm">{m.words || 0} words</span>
                </div>
                {s.text ? <div className="ep-sptext">{s.text.length > 170 ? s.text.slice(0, 170) + '…' : s.text}</div> : <div className="ep-sptext empty">No draft yet.</div>}
                <button className="ep-coachbtn" onClick={() => setCoachFor(s)}>✦ Coach with Milo</button>
              </div>
            );
          })}
        </div>
      )}

      {editing && <SpeechEditor initial={editing.item} isNew={editing.isNew} momentOpts={program.items || []} onClose={() => setEditing(null)} onSave={saveSpeech} />}
      {confirmDel && (
        <div className="ep-ov" onClick={(e) => { if (e.target.className === 'ep-ov') setConfirmDel(null); }}>
          <div className="ep-modal" style={{ maxWidth: 420 }}>
            <div className="ep-mhead"><div className="mt">Remove this speaker?</div><button className="ep-mx" onClick={() => setConfirmDel(null)}>✕</button></div>
            <div className="ep-mbody"><div className="ep-confirm">Remove <b>{confirmDel.speaker_name || 'this speaker'}</b> and their draft.</div></div>
            <div className="ep-mfoot"><button className="ep-btn" onClick={() => setConfirmDel(null)}>Keep</button><button className="ep-btn danger solid" onClick={() => removeSpeech(confirmDel)}>Remove</button></div>
          </div>
        </div>
      )}
      {coachFor && <CoachModal speech={coachFor} onClose={() => setCoachFor(null)} />}
    </div>
  );
}

function SpeechEditor({ initial, isNew, momentOpts, onClose, onSave }) {
  const [f, setF] = useState({ ...BLANK_SPEECH(), ...initial });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <div className="ep-ov" onClick={(e) => { if (e.target.className === 'ep-ov') onClose(); }}>
      <div className="ep-modal ep-draft">
        <div className="ep-mhead"><div className="mt">{isNew ? 'Add a speaker' : 'Edit speech'}</div><button className="ep-mx" onClick={onClose}>✕</button></div>
        <div className="ep-mbody">
          <div className="ep-row2">
            <div className="ep-fld"><label>Speaker</label><input className="ep-inp" value={f.speaker_name} onChange={(e) => set('speaker_name', e.target.value)} placeholder="e.g. Ana" autoFocus /></div>
            <div className="ep-fld"><label>Role</label><input className="ep-inp" value={f.role} onChange={(e) => set('role', e.target.value)} placeholder="Daughter · Best friend · MC" /></div>
          </div>
          <div className="ep-row2">
            <div className="ep-fld"><label>Target length (min)</label><input className="ep-inp" type="number" min="0" step="0.5" value={f.target_minutes} onChange={(e) => set('target_minutes', e.target.value)} /></div>
            <div className="ep-fld"><label>Part of</label>
              <select className="ep-inp" value={f.moment_id || ''} onChange={(e) => set('moment_id', e.target.value)}>
                <option value="">— not linked —</option>
                {momentOpts.map((it) => <option key={it.id} value={it.id}>{it.title || 'Untitled'}</option>)}
              </select>
            </div>
          </div>
          <div className="ep-fld"><label>The toast</label><textarea className="ep-inp ep-ta" style={{ minHeight: 160 }} value={f.text} onChange={(e) => set('text', e.target.value)} placeholder="Write or paste the speech here…" /></div>
        </div>
        <div className="ep-mfoot"><button className="ep-btn" onClick={onClose}>Cancel</button><button className="ep-btn gold" onClick={() => onSave(f)}>Save</button></div>
      </div>
    </div>
  );
}

function CoachModal({ speech, onClose }) {
  const [state, setState] = useState('loading');   // loading | ok | nokey | error
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await axios.post(`${API}/api/program/speeches/${speech.id}/coach`, {}, { headers: ADMIN_AUTH() });
        if (!live) return;
        setData(r.data);
        if (r.data.ok && r.data.coaching) setState('ok');
        else if (r.data.configured === false) setState('nokey');
        else { setErr(r.data.error || 'Could not coach this speech.'); setState('error'); }
      } catch (e) { if (live) { setErr('Something went wrong reaching Milo.'); setState('error'); } }
    })();
    return () => { live = false; };
  }, [speech.id]);

  const m = (data && data.metrics) || speech.metrics || {};
  const c = (data && data.coaching) || {};
  return (
    <div className="ep-ov" onClick={(e) => { if (e.target.className === 'ep-ov') onClose(); }}>
      <div className="ep-modal ep-draft">
        <div className="ep-mhead"><div className="mt"><span className="ep-orb sm">M</span> Coaching · {speech.speaker_name || 'speech'}</div><button className="ep-mx" onClick={onClose}>✕</button></div>
        <div className="ep-mbody">
          <div className="ep-spmeta" style={{ marginBottom: 14 }}>
            <span className="ep-vpill">{m.verdict || '—'}</span>
            <span className="ep-spm">{m.est_minutes || 0} min spoken{m.target_minutes ? ` · ${m.target_minutes} target` : ''}</span>
            <span className="ep-spm">{m.words || 0} words</span>
          </div>
          {state === 'loading' && <div className="ep-loading">Milo is reading the toast…</div>}
          {state === 'nokey' && <div className="ep-ph" style={{ padding: '14px 6px' }}><div className="pt">Coaching needs Milo's AI brain</div><div className="pd">Add an <code>ANTHROPIC_API_KEY</code> to enable written coaching. The timing above is always computed for you.</div></div>}
          {state === 'error' && <div className="ep-derr">{err}</div>}
          {state === 'ok' && (
            <>
              {c.overall && <div className="ep-dsum"><b>{c.overall}.</b> {c.summary}</div>}
              {(c.strengths || []).length > 0 && <><div className="ep-seclab">What works</div>{c.strengths.map((s, i) => <div className="ep-ins-row" key={i}><span className="ic">✓</span><span>{s}</span></div>)}</>}
              {(c.suggestions || []).length > 0 && <><div className="ep-seclab">Make it stronger</div>{c.suggestions.map((s, i) => <div className="ep-ins-row" key={i}><span className="ic">→</span><span>{s}</span></div>)}</>}
              {c.timing_note && <><div className="ep-seclab">Timing</div><div className="ep-ins-row"><span className="ic">◷</span><span>{c.timing_note}</span></div></>}
              {(c.stronger_opening || c.stronger_closing) && <div className="ep-seclab">Lines to try</div>}
              {c.stronger_opening && <div className="ep-sug"><div className="st">Open: “{c.stronger_opening}”</div></div>}
              {c.stronger_closing && <div className="ep-sug"><div className="st">Close: “{c.stronger_closing}”</div></div>}
            </>
          )}
        </div>
        <div className="ep-mfoot"><button className="ep-btn" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

/* ---- Share (Phase 6) — export on-device: PDF · copy · per-person cue sheets ---- */
function escapeHtml(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }

function ShareView({ program }) {
  const [copied, setCopied] = useState('');
  const items = program.items || [];
  const summary = program.summary || {};
  const flash = (key) => { setCopied(key); setTimeout(() => setCopied(''), 1600); };
  const lineFor = (it) => `${hhmmTo12(it.resolved_start)}  ${it.title || 'Moment'}${it.duration_min ? `  (${it.duration_min}m)` : ''}${(it.owners && it.owners.length) ? `  — ${it.owners.map((o) => o.name).join(', ')}` : ''}`;
  const fullText = () => {
    const head = `EVENT PROGRAM${program.program_date ? ` · ${program.program_date}` : ''}\n${'='.repeat(30)}\n`;
    const tail = summary.computed_end ? `\n\nRuns ${hhmmTo12(program.program_start_time)} – ${hhmmTo12(summary.computed_end)} · ${items.length} moments` : '';
    return head + items.map(lineFor).join('\n') + tail;
  };
  const cueFor = (name) => `${name.toUpperCase()} — your moments\n` +
    items.filter((it) => (it.owners || []).some((o) => o.name === name)).map(lineFor).join('\n');
  const copy = async (text, key) => { try { await navigator.clipboard.writeText(text); flash(key); } catch (e) { /* clipboard blocked */ } };

  const owners = Array.from(new Set(items.flatMap((it) => (it.owners || []).map((o) => o.name)).filter(Boolean)));

  const print = () => {
    const rows = items.map((it) => `<tr><td class="t">${hhmmTo12(it.resolved_start)}</td><td>${escapeHtml(it.title)}${it.start_mode === 'hard' ? ' &#11045;' : ''}</td><td class="d">${it.duration_min || 0}m</td><td class="o">${escapeHtml((it.owners || []).map((o) => o.name).join(', '))}</td></tr>`).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Event Program</title><style>
      body{font-family:Georgia,'Times New Roman',serif;color:#2A2622;margin:48px;max-width:720px}
      h1{font-size:27px;font-weight:500;margin:0 0 3px}
      .sub{color:#988F80;font-size:12px;margin-bottom:26px;font-family:Helvetica,Arial,sans-serif;letter-spacing:.08em;text-transform:uppercase}
      table{width:100%;border-collapse:collapse}
      td{padding:9px 6px;border-bottom:1px solid #ece6d8;font-size:14px;vertical-align:top}
      td.t{white-space:nowrap;color:#9C7B31;font-weight:bold;width:84px}
      td.d{text-align:right;color:#988F80;white-space:nowrap;width:48px}
      td.o{color:#6F685C;font-size:12px;font-family:Helvetica,Arial,sans-serif}
      .ft{margin-top:22px;color:#988F80;font-size:12px;font-style:italic}
    </style></head><body><h1>The Evening</h1><div class="sub">Event Program${program.program_date ? ` · ${escapeHtml(program.program_date)}` : ''}</div>
    <table>${rows}</table>${summary.computed_end ? `<div class="ft">Runs ${hhmmTo12(program.program_start_time)} – ${hhmmTo12(summary.computed_end)} · ${items.length} moments</div>` : ''}</body></html>`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html); win.document.close(); win.focus();
    setTimeout(() => { try { win.print(); } catch (e) { /* ignore */ } }, 300);
  };

  if (items.length === 0) {
    return <div className="ep-ph"><div className="pt">Share</div><div className="pd">Build your program first — then export it as a clean PDF, copy it to share anywhere, or hand each person their cue sheet.</div></div>;
  }
  return (
    <div className="ep-share">
      <div className="ep-shead"><div><div className="ep-he">Carry It Out</div><h2 className="ep-sh2">Share the evening</h2></div></div>
      <div className="ep-sharerow">
        <button className="ep-btn gold" onClick={print}>🖨 Print / Save as PDF</button>
        <button className="ep-btn" onClick={() => copy(fullText(), 'all')}>{copied === 'all' ? '✓ Copied' : '⧉ Copy run-of-show'}</button>
      </div>

      {owners.length > 0 && (
        <>
          <div className="ep-seclab">Cue sheets — each person's part</div>
          <div className="ep-cuegrid">
            {owners.map((name) => (
              <div className="ep-cue" key={name}>
                <div><div className="ep-cuename">{name}</div><div className="ep-cuecount">{items.filter((it) => (it.owners || []).some((o) => o.name === name)).length} moments</div></div>
                <button className="ep-coachbtn" onClick={() => copy(cueFor(name), name)}>{copied === name ? '✓ Copied' : '⧉ Copy'}</button>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="ep-seclab">Coming next</div>
      <div className="ep-soon">
        <div className="ep-soonrow"><b>Public share link</b> — a read-only page for the family. Governed: opt-in and revocable.</div>
        <div className="ep-soonrow"><b>Email vendors their parts</b> — sends through Guest Communications, with your approval before anything goes out.</div>
        <div className="ep-soonrow"><b>Publish to Calendar</b> — drop the program onto the event calendar as timed entries.</div>
      </div>
      <div className="ep-note">Printing and copying happen on your device — nothing is sent or made public.</div>
    </div>
  );
}
